import { useState, useEffect } from "react";
import {  Loader2, AlertCircle } from "lucide-react";
import api from "../../../configs/api";
import useContactModal from "../../../hooks/useContactModal";
import PropertyFilter from "../../../components/public/userListingAllProperties/PropertyFilter";
import ContactOwnerModal from "../../../components/modals/ContactOwnerModal";
import PropertyCard from "../../../components/public/userCommon/PropertyCard";
import { TrackImpression } from "../../../components/public/trackers/TrackImpression";
import { filterProperties } from "../../../utils/filterProperties";

const ListingAllProperties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [propertyData, setPropertyData] = useState({});
  const { closeModal, isOpen, openModal } = useContactModal();
  const [budgetRange, setBudgetRange] = useState([100, 100000000]);
  const [sizeRange, setSizeRange] = useState([10, 10000]);

  const [filters, setFilters] = useState({
    availabilityStatus: "",
    isFeatured: false,
    isApproved: false,
    propertyTypes: [],
    bedrooms: [],
    furnishedStatus: [],
    amenities: [],
    withPhotos: false,
    withVideos: false,
    budgetMin: 100,
    budgetMax: 100000000,
    sizeMin: 10,
    sizeMax: 10000,
  });

  // toggle filter
  const toggleFilter = (filterType, value) => {
    setFilters((prev) => {
      const currentArray = prev[filterType] || [];
      const isSelected = currentArray.includes(value);

      const newFilters = {
        ...prev,
        [filterType]: isSelected
          ? currentArray.filter((item) => item !== value)
          : [...currentArray, value],
      };

      return newFilters;
    });
  };

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();

      if (selectedCategory !== "all") params.append("type", selectedCategory);
      if (filters.isFeatured) params.append("isFeatured", "true");
      if (filters.isApproved) params.append("isApproved", "true");

      const url = `/properties${params.toString() ? `?${params}` : ""}`;

      const response = await api.get(url);
      if (response.data.success === false)
        throw new Error(response.data.message);

      let fetched = response.data.data || [];

      fetched = filterProperties({
        properties: fetched,
        filters,
        budgetRange,
        sizeRange,
      });

      setProperties(fetched);
    } catch (err) {
      console.error("ERROR:", err);
      setError(err.response?.data?.message || err.message);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      budgetMin: budgetRange[0],
      budgetMax: budgetRange[1],
      sizeMin: sizeRange[0],
      sizeMax: sizeRange[1],
    }));
  }, [budgetRange, sizeRange]);

  // Fetch properties on component mount and when filters change
  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, filters, budgetRange, sizeRange]);

  const applyFilters = () => {
    fetchProperties();
  };

  // Reset Filter
  const resetFilters = () => {
    setSelectedCategory("all");
    setBudgetRange([100, 100000000]);
    setSizeRange([10, 10000]);
    setFilters({
      availabilityStatus: "",
      isFeatured: false,
      isApproved: false,
      propertyTypes: [],
      bedrooms: [],
      furnishedStatus: [],
      amenities: [],
      withPhotos: false,
      withVideos: false,
      budgetMin: 100,
      budgetMax: 100000000,
      sizeMin: 10,
      sizeMax: 10000,
    });
  };

  const filteredPropertiesByCategory =
    selectedCategory === "all"
      ? properties
      : properties.filter((p) => p.type === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-50 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-4 px-4 sm:px-6 md:px-8 lg:px-10 py-10 gap-3 md:gap-4 relative">
        {/* Property Filter Section */}
        <div className="col-span-1 sticky top-0 z-10">
          <PropertyFilter
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            filters={filters}
            setFilters={setFilters}
            applyFilters={applyFilters}
            resetFilters={resetFilters}
            budgetRange={budgetRange}
            setBudgetRange={setBudgetRange}
            sizeRange={sizeRange}
            setSizeRange={setSizeRange}
            toggleFilter={toggleFilter}
          />
        </div>

        {/* Content Section */}
        <div className="col-span-1 lg:col-span-3 order-2 lg:order-none relative z-0">
          {/* Header */}
          <div className="mb-4 sm:mb-6 md:mb-8">
            <h1 className="font-medium text-lg sm:text-xl md:text-2xl mb-1 sm:mb-2 md:mb-3 lg:mb-4">
              {selectedCategory === "land"
                ? `${filteredPropertiesByCategory.length}+ Results | Land Property for Sale`
                : selectedCategory === "commercial"
                ? `${filteredPropertiesByCategory.length}+ Results | Commercial Property for Sale`
                : `Showing ${filteredPropertiesByCategory.length} ${
                    selectedCategory === "all"
                      ? "Properties"
                      : selectedCategory.charAt(0).toUpperCase() +
                        selectedCategory.slice(1) +
                        " Properties"
                  }`}
            </h1>

            {selectedCategory === "land" ? (
              <p className="text-sm md:text-md lg:text-lg font-normal text-gray-600">
                Discover the best plots with flexible options, securely listed
                for your convenience.
              </p>
            ) : selectedCategory === "commercial" ? (
              <p className="text-sm md:text-md lg:text-lg font-normal text-gray-600">
                Explore premium commercial properties with excellent
                connectivity and amenities.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <span className="bg-white border border-gray-300 px-3 py-1.5 sm:px-4 sm:py-2 rounded-3xl text-xs sm:text-sm text-gray-700">
                  {filteredPropertiesByCategory.length} Results
                </span>
                {filters.isFeatured && (
                  <span className="bg-blue-50 border border-blue-300 px-3 py-1.5 sm:px-4 sm:py-2 rounded-3xl text-xs sm:text-sm text-blue-700">
                    Featured Only
                  </span>
                )}
                {filters.isApproved && (
                  <span className="bg-green-50 border border-green-300 px-3 py-1.5 sm:px-4 sm:py-2 rounded-3xl text-xs sm:text-sm text-green-700">
                    Verified Only
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
              <p className="text-gray-600 text-lg">Loading properties...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-red-800 font-semibold mb-1">
                  Error Loading Properties
                </h3>
                <p className="text-red-600 text-sm mb-3">{error}</p>
                <button
                  onClick={fetchProperties}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Properties List */}
          {!loading && !error && (
            <div
              className="
                  grid 
                  grid-cols-1 
                  sm:grid-cols-2
                  md:grid-cols-1 
                  gap-4 
                  sm:gap-5 
                  md:gap-6
                      "
            >
              {filteredPropertiesByCategory.length > 0 ? (
                filteredPropertiesByCategory.map((p) => (
                  <TrackImpression key={p._id} propertyId={p._id}>
                    <PropertyCard
                      property={p}
                      openModal={openModal}
                      setPropertyData={setPropertyData}
                    />
                  </TrackImpression>
                ))
              ) : (
                <div className="col-span-full bg-white rounded-lg shadow-md p-8 text-center">
                  <div className="flex flex-col items-center justify-center py-8">
                    <AlertCircle className="w-16 h-16 text-gray-400 mb-4" />
                    <p className="text-gray-500 text-lg mb-2">
                      No properties found
                    </p>
                    <p className="text-gray-400 text-sm mb-4">
                      {selectedCategory === "all"
                        ? "Try adjusting your filters or check back later"
                        : `No ${selectedCategory} properties match your criteria`}
                    </p>
                    <button
                      onClick={resetFilters}
                      className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ContactOwnerModal
        propertyData={propertyData}
        isOpen={isOpen}
        onClose={closeModal}
      />
    </div>
  );
};

export default ListingAllProperties;
